import { ContentfulFAQ, getContentfulFAQ } from 'api/contentful/getContentfulFAQ'
import PlanPerks from 'components/content/planPerks/PlanPerks'
import { CyberscoutHeroSection } from 'components/cyberscout/CyberscoutHeroSection'
import { CyberscoutPageSections } from 'components/cyberscout/CyberscoutPageSections'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { IconName } from 'components/icon'
import { Layout } from 'components/layout/Layout'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { Signup, ToneContext } from 'components/signup/Signup'
import { Spacer } from 'components/spacer/Spacer'
import { Statistics } from 'components/statistics/Statistics'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import React from 'react'
import { REVALIDATE_SECS } from 'utils/constants'
import { getContentfulImages } from 'utils/contentful/getPageImages'
import { numberRange } from 'utils/general'
import { getIntlProps } from 'utils/i18n/getIntlProps'
import { mapKeysToTrans } from 'utils/i18n/mapKeysToTrans'
import { NumberRange } from 'utils/types'

interface CyberscoutPageStaticProps {
  faq: ContentfulFAQ
}

const statisticIcons: Record<NumberRange<3>, IconName> = {
  '1': 'Skull',
  '2': 'IdentityBadge',
  '3': 'ShieldCross',
}

export default function Page(props: CyberscoutPageStaticProps) {
  const { t } = useTranslation('cyberscout')

  const signupPerks = mapKeysToTrans(
    t,
    numberRange(4).map((i) => `cyberscout.section_signup.perk_${i}` as const)
  )

  return (
    <Layout
      header={<TopNavigationLandingPage variant="full" noSpacingBelow />}
      footer={<Footer variant="full" />}
    >
      <PageTitle title={t('cyberscout.meta_title')} hideDefaultSuffix />
      <PageMeta
        description={t('cyberscout.meta_description')}
        image="/images/preview/cyberscout.png"
      />

      <CyberscoutHeroSection />

      <Spacer size={[2, 6]} />

      <Statistics
        title={t('cyberscout.statistics.title')}
        items={numberRange(3).map((i) => ({
          title: t(`cyberscout.statistics.item_${i}.title`),
          description: t(`cyberscout.statistics.item_${i}.desc`),
          icon: statisticIcons[i],
        }))}
      />

      <Spacer size={[2, 6]} />

      <CyberscoutPageSections />

      <PlanPerks />

      <Spacer size={[2, 6]} />

      <FaqTwoColumns content={props.faq} title={t('cyberscout.faq_title')}></FaqTwoColumns>

      <Spacer size={[2, 6]} />

      <ToneContext.Provider value="dark">
        <Signup
          caption={t('cyberscout.section_signup.caption')}
          title={t('cyberscout.section_signup.title')}
          perks={signupPerks}
        />
      </ToneContext.Provider>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps<CyberscoutPageStaticProps> = async (ctx) => {
  const faq = await getContentfulFAQ('cyberscout')

  const props: CyberscoutPageStaticProps = {
    ...(await getContentfulImages()),
    ...(await getIntlProps(ctx, ['cyberscout', 'pricing_plan'])),
    faq,
  }

  return { props, revalidate: REVALIDATE_SECS }
}
